/**
 * Rooms travel as URL-safe text.
 *
 * A share link that carries the whole room means the viral loop works before a
 * single line of backend exists: "你绝对过不了" is a link, not an account.
 * When the server lands, links shorten to `#r=<id>` and this codec stays as
 * the offline/fallback path — so it must never silently mutate a room.
 */

import type { PropInstance, RoomDef, Rot } from './room'
import { ROOM_VERSION } from './room'
import { PROP_ORDER, isPropTypeId } from './props'
import { parseRoom } from './validate'

/** Compact tuple form. Field order is frozen; append only. */
type Packed = [
  v: number,
  id: string,
  name: string,
  ownerName: string,
  taunt: string,
  seed: number,
  width: number,
  depth: number,
  chestX: number,
  chestZ: number,
  doorX: number,
  doorZ: number,
  createdAt: number,
  props: number[],
]

/** One prop → one 16-bit int: type(4) | x(5) | z(5) | rot(2). */
function packProp(p: PropInstance): number {
  const t = PROP_ORDER.indexOf(p.type)
  return ((t & 0xf) << 12) | ((p.x & 0x1f) << 7) | ((p.z & 0x1f) << 2) | (p.rot & 0x3)
}

function unpackProp(n: number, iid: number): PropInstance | null {
  const t = PROP_ORDER[(n >> 12) & 0xf]
  if (!t || !isPropTypeId(t)) return null
  return {
    iid,
    type: t,
    x: (n >> 7) & 0x1f,
    z: (n >> 2) & 0x1f,
    rot: (n & 0x3) as Rot,
  }
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let latin1 = ''
  for (const b of bytes) latin1 += String.fromCharCode(b)
  return btoa(latin1).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlToBytes(code: string): Uint8Array | null {
  const normalised = code.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalised + '='.repeat((4 - (normalised.length % 4)) % 4)
  try {
    const latin1 = atob(padded)
    const out = new Uint8Array(latin1.length)
    for (let i = 0; i < latin1.length; i++) out[i] = latin1.charCodeAt(i)
    return out
  } catch {
    return null
  }
}

export function encodeRoom(room: RoomDef): string {
  const packed: Packed = [
    ROOM_VERSION,
    room.id,
    room.name,
    room.ownerName,
    room.taunt,
    room.seed,
    room.width,
    room.depth,
    room.chest.x,
    room.chest.z,
    room.door.x,
    room.door.z,
    room.createdAt,
    room.props.map(packProp),
  ]
  return bytesToBase64Url(new TextEncoder().encode(JSON.stringify(packed)))
}

export function decodeRoom(code: string): RoomDef | null {
  const bytes = base64UrlToBytes(code.trim())
  if (!bytes) return null
  let packed: unknown
  try {
    packed = JSON.parse(new TextDecoder().decode(bytes))
  } catch {
    return null
  }
  if (!Array.isArray(packed) || packed.length < 14) return null
  const [v, id, name, ownerName, taunt, seed, width, depth, cx, cz, dx, dz, createdAt, rawProps] =
    packed as Packed
  if (v !== ROOM_VERSION) return null

  const props: PropInstance[] = []
  if (Array.isArray(rawProps)) {
    rawProps.forEach((n, i) => {
      if (typeof n !== 'number') return
      const p = unpackProp(n, i + 1)
      if (p) props.push(p)
    })
  }

  // Route back through the untrusted-input parser so a hand-edited link can
  // never produce a room the sim has not vetted.
  return parseRoom({
    v: ROOM_VERSION,
    id,
    name,
    ownerName,
    taunt,
    seed,
    width,
    depth,
    props,
    chest: { x: cx, z: cz },
    door: { x: dx, z: dz },
    createdAt,
  })
}

/** Full share URL for a room, honouring whatever origin/path we are served from. */
export function roomShareUrl(room: RoomDef, origin = location.origin + location.pathname): string {
  return `${origin}#r=${encodeRoom(room)}`
}

/** Read a room out of the current URL hash, if any. */
export function roomFromHash(hash = location.hash): RoomDef | null {
  const m = /[#&]r=([A-Za-z0-9\-_]+)/.exec(hash)
  if (!m || !m[1]) return null
  return decodeRoom(m[1])
}
